/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'classnames';
import { useFormik } from 'formik';
import humanize from 'humanize-plus';
import _ from 'lodash';
import { DateTime } from 'luxon';
import { memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { queryCache, useQuery } from 'react-query';

import CheckIcon from '@mui/icons-material/Check';
import {
  Button,
  CircularProgress,
  Collapse,
  Divider,
  Fade,
  FormControl,
  FormHelperText,
  Grid,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Switch,
  Typography,
  Zoom,
} from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers';

import { useUpdateCluster } from '../../../../../../components/hooks/useUpdateCluster';
import { SimpleConfirmModal } from '../../../../../../components/SimpleConfirmModal/SimpleConfirmModal';
import { SimpleErrorModal } from '../../../../../../components/SimpleErrorModal/SimpleErrorModal';
import { Namespaces } from '../../../../../../i18n';
import { useBackupsConfigureStyles } from '../../../../../../styles/backupsConfigure';
import { useFormStyles } from '../../../../../../styles/form';
import { ClusterContinuousBackup } from '../../../../../../types/cluster/ClusterContinuousBackup';
import { ClusterDetailsResponsePayload } from '../../../../../../types/cluster/ClusterDetailsResponsePayload';
import { UpdateClusterDetailsParameterIdentification } from '../../../../../../types/cluster/UpdateClusterDetailsParameterIdentification';
import { formatBackupDateTimeWithTimezone } from '../../../../../../utils/formatBackupDateTimeWithTimezone';
import {
  fetchContinuousBackupLatestRestorePoint,
  fetchContinuousBackupOldestRestorePoint,
} from '../../../../../../utils/queryFns';
import { useObjectState } from '../../../../../../utils/useObjectState';
import { useProjectPermissions } from '../../../../../../utils/useProjectPermissions';
import { ConfigUpdateState } from '../BackupsLogical/BackupsLogical.types';
import { validationSchema } from './BackupsContinuous.consts';

type FormikValuesContinuous = {
  enabled: boolean;
  scheduleTime: DateTime;
  daysToRetain: number;
};

type Props = {
  identificationData: UpdateClusterDetailsParameterIdentification;
  details: ClusterDetailsResponsePayload;
  isSpinning: boolean;
  continuousBackups: ClusterContinuousBackup[];
};

export const BackupsContinuous = memo<Props>(props => {
  const { identificationData, details, isSpinning, continuousBackups } = props;

  const permissions = useProjectPermissions(identificationData.organizationId, identificationData.projectId);

  const classes = useBackupsConfigureStyles();
  const formClasses = useFormStyles();
  const { t } = useTranslation(Namespaces.Cluster);
  const [isOpenErrorModal, setIsOpenErrorModal] = useState<boolean>(false);
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    data?: FormikValuesContinuous;
  }>({
    isOpen: false,
    data: undefined,
  });
  const [updateCluster] = useUpdateCluster();

  const [configUpdateState, setConfigUpdateState] = useObjectState<ConfigUpdateState>({
    isProcessing: false,
    isLoading: false,
    isSuccess: false,
  });

  const latestRestorePointQuery = useQuery(
    [
      fetchContinuousBackupLatestRestorePoint.key,
      {
        ...identificationData,
      },
    ],
    fetchContinuousBackupLatestRestorePoint.fn,
    { retry: false },
  );

  const oldestRestorePointQuery = useQuery(
    [
      fetchContinuousBackupOldestRestorePoint.key,
      {
        ...identificationData,
      },
    ],
    fetchContinuousBackupOldestRestorePoint.fn,
    { retry: false },
  );

  const latestRestorePoint = useMemo(
    () => latestRestorePointQuery.data?.payload || null,
    [latestRestorePointQuery.data],
  );

  const oldestRestorePoint = useMemo(
    () => oldestRestorePointQuery.data?.payload || null,
    [oldestRestorePointQuery.data],
  );

  const oldestRestorePointQueryError = useMemo(() => {
    return oldestRestorePointQuery.status === 'error' || oldestRestorePointQuery.data?.message === 'error';
  }, [oldestRestorePointQuery.data, oldestRestorePointQuery.status]);

  // BackupSize
  const continuosBackupsSize = useMemo(
    () => continuousBackups.map(obj => obj.sizeInBytes).reduce((prev, next) => prev + next, 0),
    [continuousBackups],
  );

  const continuosBackupsFullSize = useMemo(() => humanize.fileSize(continuosBackupsSize), [continuosBackupsSize]);

  const { enabled: isEnableInit, daysToRetain } = details.backupConfiguration.continuous;

  const schedule = useMemo<string[]>(() => {
    return details.backupConfiguration.continuous.schedule.split(' ');
  }, [details]);

  const scheduleTime = useMemo<DateTime>(
    () =>
      DateTime.utc().set({
        hour: +schedule[1],
        minute: +schedule[0],
        second: 0,
        millisecond: 0,
      }),
    [schedule],
  );

  const latestRestorePointDatetime = latestRestorePoint?.lastModified
    ? DateTime.fromISO(latestRestorePoint.lastModified)
    : DateTime.now();

  const oldestRestorePointDatetime = oldestRestorePoint?.lastModified
    ? DateTime.fromISO(oldestRestorePoint.lastModified)
    : DateTime.now();

  const { lastRestorePointLocal, lastRestorePointServer } = useMemo(() => {
    return {
      lastRestorePointLocal: formatBackupDateTimeWithTimezone({ dateTime: oldestRestorePointDatetime }),
      lastRestorePointServer: formatBackupDateTimeWithTimezone({ dateTime: oldestRestorePointDatetime, utc: true }),
    };
  }, [oldestRestorePointDatetime]);

  const nextBackupUtc = useMemo(
    () =>
      formatBackupDateTimeWithTimezone({
        utc: true,
        dateTime: latestRestorePointDatetime,
        schedule: { minutes: +schedule[0], hours: +schedule[1] },
      }),
    [schedule, latestRestorePointDatetime],
  );

  const nextBackupLocal = useMemo(
    () =>
      formatBackupDateTimeWithTimezone({
        dateTime: latestRestorePointDatetime,
        schedule: { minutes: +schedule[0], hours: +schedule[1] },
      }),
    [schedule, latestRestorePointDatetime],
  );

  const handleFormSubmit = async (values: FormikValuesContinuous) => {
    const continuousData = !values.enabled
      ? {
          enabled: false,
        }
      : {
          enabled: values.enabled,
          schedule: `${values.scheduleTime.toFormat('mm')} ${values.scheduleTime.toFormat('HH')} * * *`,
          daysToRetain: Number(values.daysToRetain),
        };
    try {
      setConfigUpdateState({ ...configUpdateState, isProcessing: true, isLoading: true });
      await updateCluster({
        ...identificationData,
        data: {
          configuration: {
            backup: {
              continuous: continuousData,
            },
          },
        },
      });
      queryCache.removeQueries(
        ({ queryKey }) =>
          queryKey[0] === fetchContinuousBackupOldestRestorePoint.key &&
          _.get(queryKey[1], 'clusterId') === identificationData.clusterId,
      );
    } catch (e) {
      setIsOpenErrorModal(true);
    } finally {
      setTimeout(() => setConfigUpdateState({ ...configUpdateState, isProcessing: false }), 4000);
    }
  };

  const initialValues = useMemo<FormikValuesContinuous>(
    () => ({
      enabled: isEnableInit,
      scheduleTime: scheduleTime,
      daysToRetain: daysToRetain,
    }),
    [daysToRetain, isEnableInit, scheduleTime],
  );

  const handleSubmitModal = (values: FormikValuesContinuous) => {
    if (!values.enabled && initialValues.enabled !== values.enabled) {
      setConfirmModal({ isOpen: true, data: values });
    } else {
      void handleFormSubmit(values);
    }
  };

  const formik = useFormik<FormikValuesContinuous>({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: handleSubmitModal,
  });

  const { setFieldValue, setFieldTouched, handleSubmit, values, handleChange, errors, dirty } = formik;

  const handleTimeChange = (value: DateTime | null) => {
    if (!value) {
      void setFieldValue('scheduleTime', null);
    } else {
      void setFieldValue('scheduleTime', value);
    }
    void setFieldTouched('scheduleTime', true, false);
  };

  useEffect(() => {
    if (!dirty) {
      setConfirmModal({ isOpen: false, data: undefined });
    }
  }, [dirty, initialValues]);

  useEffect(() => {
    if (configUpdateState.isLoading) {
      setTimeout(() => setConfigUpdateState({ ...configUpdateState, isLoading: false, isSuccess: true }), 2000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configUpdateState.isLoading]);

  const isDisabled = !permissions.edit_cluster || isSpinning || configUpdateState.isProcessing;

  return (
    <div className={classes.root}>
      <form onSubmit={handleSubmit}>
        <Grid item classes={{ root: 'd-flex' }}>
          <div>
            <Grid item classes={{ root: 'd-flex' }}>
              <Switch
                name="enabled"
                checked={values.enabled}
                onChange={handleChange}
                disabled={isDisabled}
                aria-disabled={isDisabled}
                data-cy="cluster-page-enable-continuous-backup"
                sx={{ mr: '20px' }}
              />
              <Typography variant="h3" classes={{ root: classes.heading }}>
                {t('details.backups.continuous_backups_tab_title')}
              </Typography>
            </Grid>
            <Grid item classes={{ root: 'd-flex' }} sx={{ mt: '16px', alignItems: 'center' }}>
              <FormControl className={cx(classes.formControl, formClasses.formControl, formClasses.widthMedium)}>
                <TimePicker
                  label={t('details.backups.backups_time')}
                  value={values.scheduleTime}
                  ampm={false}
                  timezone="UTC"
                  onChange={value => handleTimeChange(value)}
                  disabled={!values.enabled || isDisabled}
                  readOnly={!permissions.edit_cluster}
                />
              </FormControl>
              <FormControl
                className={cx(classes.formControl, formClasses.formControl, formClasses.widthMedium)}
                error={!!errors.daysToRetain}
              >
                <InputLabel htmlFor="daysToRetain">{t('details.backups.retention_period')}</InputLabel>
                <OutlinedInput
                  label={t('details.backups.retention_period')}
                  name="daysToRetain"
                  error={!!errors.daysToRetain}
                  fullWidth
                  type="text"
                  value={values.daysToRetain}
                  endAdornment={<InputAdornment position="start">{t('details.backups.days')}</InputAdornment>}
                  onChange={handleChange}
                  disabled={!values.enabled || isDisabled}
                  readOnly={!permissions.edit_cluster}
                />
                {!!errors.daysToRetain && (
                  <FormHelperText>{t('details.backups.retention_period_not_valid')}</FormHelperText>
                )}
              </FormControl>
              <Fade in={dirty || configUpdateState.isProcessing}>
                <Button
                  variant="contained"
                  type="submit"
                  disabled={isDisabled}
                  data-cy="cluster-page-continuous-backup-apply-button"
                >
                  {configUpdateState.isSuccess ? (
                    <Zoom in>
                      <CheckIcon color="primary" sx={{ fontSize: '24px' }} />
                    </Zoom>
                  ) : configUpdateState.isLoading ? (
                    <Zoom in>
                      <CircularProgress size={24} />
                    </Zoom>
                  ) : (
                    t('apply')
                  )}
                </Button>
              </Fade>
            </Grid>
            <Collapse in={values.enabled}>
              <Divider component="div" sx={{ mt: '16px', mb: '16px' }} />
              <Grid item classes={{ root: 'd-flex' }}>
                <div>
                  <Typography variant={'h3'} classes={{ root: classes.h3 }}>
                    {t('details.backups.next_backup_scheduled')}
                  </Typography>
                  <div className={classes.timeContainer}>
                    <div style={{ marginRight: '3px' }}>
                      <Typography className={classes.timeLabel}>{t('details.backups.server_time')}:</Typography>
                      <Typography className={classes.timeLabel}>{t('details.backups.your_local_time')}:</Typography>
                    </div>
                    <div>
                      <Typography className={classes.timeLabel}>{nextBackupUtc}</Typography>
                      <Typography className={classes.timeLabel}>{nextBackupLocal}</Typography>
                    </div>
                  </div>
                </div>
                <div style={{ marginLeft: '20px' }}>
                  <Typography variant={'h3'} classes={{ root: classes.h3 }}>
                    {t('details.backups.oldest_possible_restore_point')}
                  </Typography>
                  {oldestRestorePoint && !oldestRestorePointQueryError ? (
                    <>
                      <div className={classes.timeContainer}>
                        <div style={{ marginRight: '3px' }}>
                          <Typography className={classes.timeLabel}>{t('details.backups.server_time')}:</Typography>
                          <Typography className={classes.timeLabel}>{t('details.backups.your_local_time')}:</Typography>
                        </div>
                        <div>
                          <Typography className={classes.timeLabel}>{lastRestorePointServer}</Typography>
                          <Typography className={classes.timeLabel}>{lastRestorePointLocal}</Typography>
                        </div>
                      </div>
                      <Typography className={cx(classes.restoreSize, classes.timeLabel)}>
                        {t('details.backups.base_backup_size', {
                          currentBaseSize: continuosBackupsFullSize,
                        })}
                      </Typography>
                    </>
                  ) : (
                    <div className={classes.timeContainer}>
                      <Typography className={classes.timeLabel}>{t('details.backups.no_base_backup')}</Typography>
                    </div>
                  )}
                </div>
              </Grid>
            </Collapse>
          </div>
        </Grid>
      </form>
      <SimpleErrorModal open={isOpenErrorModal} onOk={() => setIsOpenErrorModal(false)} />
      <SimpleConfirmModal
        open={confirmModal.isOpen}
        message={t('details.backups.confirm_disable_continuous_backups')}
        onConfirm={() => {
          void handleFormSubmit(confirmModal.data!);
          setConfirmModal({ isOpen: false, data: undefined });
        }}
        onCancel={() => setConfirmModal({ isOpen: false, data: undefined })}
      />
    </div>
  );
});
